/**
 * Coach-mark overlay (#776): dims the page, cuts a spotlight around the step's target and shows the step card next
 * to it (a bottom sheet below 640px). Modal: the app root is made `inert`, focus is trapped in the card, Esc skips,
 * ← / → move, Enter advances. Purely presentational: the parent owns the step index and navigation.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { localeDir } from '../i18n/i18n';
import { useT } from '../i18n/useT';
import { PHONE_MAX_WIDTH, SPOT_PAD, placeCard, scrollDelta, type Placement, type Rect } from './placement';

/** How long to wait for a step's target (route change, data loading) before showing the card without a spotlight. */
const TARGET_WAIT_MS = 2500;
const POLL_MS = 120;

function visibleRect(el: Element): Rect | null {
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  const style = window.getComputedStyle(el);
  if (style.visibility === 'hidden' || style.display === 'none') return null;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function findTarget(selectors: readonly string[]): { el: Element; rect: Rect } | null {
  for (const s of selectors) {
    const el = document.querySelector(s);
    const rect = el ? visibleRect(el) : null;
    if (el && rect) return { el, rect };
  }
  return null;
}

const sameRect = (a: Rect | null, b: Rect | null) =>
  a === b ||
  (!!a &&
    !!b &&
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5);

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function useViewport() {
  const [vp, setVp] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const on = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return vp;
}

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface TourOverlayProps {
  stepKey: string;
  index: number;
  total: number;
  targets: readonly string[];
  title: string;
  body: readonly string[];
  /** Extra content under the body (the finish step's links). */
  extra?: ReactNode;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export default function TourOverlay(p: TourOverlayProps) {
  const { stepKey, index, total, targets, onNext, onBack, onSkip } = p;
  const titleId = useId();
  const bodyId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const vp = useViewport();
  const phone = vp.w < PHONE_MAX_WIDTH;
  const last = index === total - 1;
  const t = useT();

  // Target tracking: poll (routes load data asynchronously) and follow scroll/resize.
  const [rect, setRect] = useState<Rect | null>(null);
  const [timedOut, setTimedOut] = useState(targets.length === 0);
  const scrolledFor = useRef<string | null>(null);
  const targetsKey = targets.join('|');

  useEffect(() => {
    setRect(null);
    setTimedOut(targets.length === 0);
    if (targets.length === 0) return;
    const started = Date.now();
    const measure = () => {
      const found = findTarget(targets);
      setRect((prev) => (sameRect(prev, found?.rect ?? null) ? prev : (found?.rect ?? null)));
      if (!found && Date.now() - started > TARGET_WAIT_MS) setTimedOut(true);
    };
    measure();
    const id = window.setInterval(measure, POLL_MS);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
    // targetsKey stands for `targets` (a new array identity each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, targetsKey]);

  // Card size, for placement.
  const [card, setCard] = useState({ w: 360, h: 220 });
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      setCard((c) => (c.w === w && c.h === h ? c : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Bring the target into view once per step (above the bottom sheet on phones, below the sticky header).
  useEffect(() => {
    if (!rect || scrolledFor.current === stepKey) return;
    scrolledFor.current = stepKey;
    const header = document.querySelector('header');
    const topInset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const bottomInset = phone ? card.h : 0;
    const dy = scrollDelta(rect, vp.h, topInset, bottomInset, !phone);
    if (dy !== 0) window.scrollBy({ top: dy, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [rect, stepKey, phone, card.h, vp.h]);

  const waiting = !rect && !timedOut;

  // Modal: everything behind the overlay is inert while the tour runs.
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    root.setAttribute('inert', '');
    root.setAttribute('aria-hidden', 'true');
    return () => {
      root.removeAttribute('inert');
      root.removeAttribute('aria-hidden');
    };
  }, []);

  // Focus the primary button on every step.
  useEffect(() => {
    if (!waiting) nextRef.current?.focus({ preventScroll: true });
  }, [stepKey, waiting]);

  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        // The arrow that points "forward" moves on: → in left-to-right languages, ← in Arabic and Hebrew.
        e.preventDefault();
        if ((e.key === 'ArrowRight') === (localeDir() === 'ltr')) onNext();
        else onBack();
      } else if (e.key === 'Enter') {
        const a = document.activeElement;
        // Buttons and links handle Enter themselves.
        if (a instanceof HTMLButtonElement || a instanceof HTMLAnchorElement) return;
        e.preventDefault();
        onNext();
      } else if (e.key === 'Tab') {
        const items = Array.from(cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
        if (items.length === 0) return;
        const first = items[0]!;
        const lastItem = items[items.length - 1]!;
        const a = document.activeElement;
        if (e.shiftKey && (a === first || !cardRef.current?.contains(a))) {
          e.preventDefault();
          lastItem.focus();
        } else if (!e.shiftKey && (a === lastItem || !cardRef.current?.contains(a))) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onNext, onBack, onSkip],
  );
  useEffect(() => {
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onKey]);

  let placement: Placement | null = null;
  if (!phone) placement = rect ? placeCard(rect, card, vp.w, vp.h) : null;

  const motion = 'motion-safe:transition-all motion-safe:duration-200';
  let cardPos: CSSProperties | undefined;
  let cardCls: string;
  if (phone) {
    cardCls = 'inset-x-0 bottom-0 max-h-[55vh] overflow-y-auto rounded-b-none border-b-0 pb-[max(1rem,env(safe-area-inset-bottom))]';
  } else if (placement?.kind === 'floating') {
    cardCls = `w-[360px] ${motion}`;
    cardPos = { top: placement.top, left: placement.left };
  } else if (placement?.kind === 'corner') {
    cardCls = `bottom-4 end-4 w-[360px] ${motion}`;
  } else {
    // No target: centred.
    cardCls = 'left-1/2 top-1/2 w-[400px] max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2';
  }

  return createPortal(
    <div className="fixed inset-0 z-[70]" data-testid="tour" data-step={stepKey}>
      {/* Click shield: the page behind is not interactive during the tour. */}
      <div aria-hidden className={`absolute inset-0 ${rect ? '' : 'bg-black/60'}`} />
      {rect && (
        <div
          aria-hidden
          data-testid="tour-spotlight"
          className={`pointer-events-none absolute rounded-lg ring-2 ring-accent ${motion}`}
          style={{
            top: rect.top - SPOT_PAD,
            left: rect.left - SPOT_PAD,
            width: rect.width + SPOT_PAD * 2,
            height: rect.height + SPOT_PAD * 2,
            boxShadow: '0 0 0 9999px rgb(0 0 0 / 0.6)',
          }}
        />
      )}
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        data-testid="tour-card"
        className={`card absolute space-y-3 p-4 shadow-2xl ${cardCls} ${waiting ? 'invisible' : ''}`}
        style={cardPos}
      >
        <div className="flex items-center justify-between gap-2 text-xs text-muted">
          <span data-testid="tour-progress">{t('tour.ui.step_of', { n: index + 1, total })}</span>
          <button type="button" onClick={onSkip} className="rounded-md px-2 py-1 hover:bg-surface-2 hover:text-fg">
            {t('tour.ui.skip')}
          </button>
        </div>
        <h2 id={titleId} className="text-base font-semibold">
          {p.title}
        </h2>
        <div id={bodyId} className="space-y-2 text-sm text-muted">
          {p.body.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
        {p.extra}
        <div className="flex items-center gap-2 pt-1">
          <div aria-hidden className="me-auto flex gap-1">
            {Array.from({ length: total }, (_, i) => (
              <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === index ? 'bg-accent' : 'bg-surface-2'}`} />
            ))}
          </div>
          {index > 0 && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-2"
            >
              {t('tour.ui.back')}
            </button>
          )}
          <button
            ref={nextRef}
            type="button"
            onClick={onNext}
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-bg"
          >
            {last ? t('tour.ui.finish') : t('tour.ui.next')}
          </button>
        </div>
        <p className="hidden text-[11px] text-muted sm:block">{t('tour.ui.keys_hint')}</p>
      </div>
    </div>,
    document.body,
  );
}
